import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AcceptInviteApplicationErrors,
  AcceptInviteResponse,
  ApplyCouponApplicationErrors,
  ApplyCouponResponse,
  ApproveJoinApplicationApplicationErrors,
  ApproveJoinApplicationResponse,
  BulkCreateJoinApplicationsApplicationErrors,
  BulkCreateJoinApplicationsOptions,
  BulkCreateJoinApplicationsResponse,
  BulkDeleteJoinApplicationsResponse,
  BulkInviteMembersToProgramByFilterApplicationErrors,
  BulkInviteMembersToProgramByFilterOptions,
  BulkInviteMembersToProgramByFilterResponse,
  BulkUpdateJoinApplicationTagsApplicationErrors,
  BulkUpdateJoinApplicationTagsByFilterApplicationErrors,
  BulkUpdateJoinApplicationTagsByFilterOptions,
  BulkUpdateJoinApplicationTagsByFilterResponse,
  BulkUpdateJoinApplicationTagsOptions,
  BulkUpdateJoinApplicationTagsResponse,
  CancelJoinApplicationApplicationErrors,
  CancelJoinApplicationResponse,
  CompleteFreeCouponPaymentApplicationErrors,
  CompleteFreeCouponPaymentResponse,
  CreateJoinApplicationApplicationErrors,
  DeclineJoinApplicationApplicationErrors,
  DeclineJoinApplicationResponse,
  GetCurrentJoinApplicationApplicationErrors,
  GetCurrentJoinApplicationResponse,
  JoinApplication,
  JoinApplicationCreatedEnvelope,
  JoinApplicationDeletedEnvelope,
  JoinApplicationPaymentStatusChangedEnvelope,
  JoinApplicationQuery,
  JoinApplicationSearch,
  JoinApplicationStatusChangedEnvelope,
  JoinApplicationTagsModifiedEnvelope,
  JoinApplicationUpdatedEnvelope,
  JoinApplicationsQueryBuilder,
  MyJoinApplicationApplicationErrors,
  MyJoinApplicationResponse,
  PaymentTypeWithLiterals,
  PreparePaymentApplicationErrors,
  PreparePaymentResponse,
  QueryJoinApplicationsResponse,
  RemoveCouponApplicationErrors,
  RemoveCouponResponse,
  SearchJoinApplicationsResponse,
  acceptInvite as universalAcceptInvite,
  applyCoupon as universalApplyCoupon,
  approveJoinApplication as universalApproveJoinApplication,
  bulkCreateJoinApplications as universalBulkCreateJoinApplications,
  bulkDeleteJoinApplications as universalBulkDeleteJoinApplications,
  bulkInviteMembersToProgramByFilter as universalBulkInviteMembersToProgramByFilter,
  bulkUpdateJoinApplicationTags as universalBulkUpdateJoinApplicationTags,
  bulkUpdateJoinApplicationTagsByFilter as universalBulkUpdateJoinApplicationTagsByFilter,
  cancelJoinApplication as universalCancelJoinApplication,
  completeFreeCouponPayment as universalCompleteFreeCouponPayment,
  createJoinApplication as universalCreateJoinApplication,
  declineJoinApplication as universalDeclineJoinApplication,
  deleteJoinApplication as universalDeleteJoinApplication,
  getCurrentJoinApplication as universalGetCurrentJoinApplication,
  getJoinApplication as universalGetJoinApplication,
  myJoinApplication as universalMyJoinApplication,
  preparePayment as universalPreparePayment,
  queryJoinApplications as universalQueryJoinApplications,
  removeCoupon as universalRemoveCoupon,
  searchJoinApplications as universalSearchJoinApplications,
  typedQueryJoinApplications as universalTypedQueryJoinApplications,
} from './online-programs-participants-v3-join-application-join-applications.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/online-programs' };

export function createJoinApplication(
  httpClient: HttpClient
): CreateJoinApplicationSignature {
  return (joinApplication: NonNullablePaths<JoinApplication, `programId`, 2>) =>
    universalCreateJoinApplication(
      joinApplication,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateJoinApplicationSignature {
  /**
   * Creates a JoinApplication for the given program.
   *
   * Members creating their own applications cannot specify `member_id` (it is automatically set to the caller).
   * Site owners with `manage_applications` permission must provide `member_id` when creating applications on behalf of members.
   *
   * For member-initiated applications, `status` is automatically set to `APPROVAL_PENDING` (or `APPROVED` if no approval is required).
   * Site owners can specify `INVITED` or `APPROVED` status when creating applications.
   * @param - JoinApplication to be created.
   * @returns The created JoinApplication.
   */
  (joinApplication: NonNullablePaths<JoinApplication, `programId`, 2>): Promise<
    NonNullablePaths<
      JoinApplication,
      | `programId`
      | `status`
      | `paymentStatus`
      | `paymentInfo.paidPlanIds`
      | `tags.privateTags.tagIds`,
      4
    > & {
      __applicationErrorsType?: CreateJoinApplicationApplicationErrors;
    }
  >;
}

export function getJoinApplication(
  httpClient: HttpClient
): GetJoinApplicationSignature {
  return (joinApplicationId: string) =>
    universalGetJoinApplication(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetJoinApplicationSignature {
  /**
   * Retrieves a JoinApplication.
   *
   * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve any join application.
   * @param - ID of the JoinApplication to retrieve.
   * @returns The requested JoinApplication.
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      JoinApplication,
      | `programId`
      | `status`
      | `paymentStatus`
      | `paymentInfo.paidPlanIds`
      | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteJoinApplication(
  httpClient: HttpClient
): DeleteJoinApplicationSignature {
  return (joinApplicationId: string) =>
    universalDeleteJoinApplication(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteJoinApplicationSignature {
  /**
   * Deletes a JoinApplication.
   *
   * Deleting a JoinApplication permanently removes them from the JoinApplication List.
   * @param - Id of the JoinApplication to delete.
   */
  (joinApplicationId: string): Promise<void>;
}

export function queryJoinApplications(
  httpClient: HttpClient
): QueryJoinApplicationsSignature {
  return () =>
    universalQueryJoinApplications(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryJoinApplicationsSignature {
  /**
   * Retrieves a list of up to 1,000 join applications, given the provided paging, filtering, and sorting.
   *
   * Members can retrieve their own join applications. Site owners with appropriate permissions can retrieve applications across all members.
   *
   * Query Join Applications runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * To learn about working with query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   */
  (): JoinApplicationsQueryBuilder;
}

export function typedQueryJoinApplications(
  httpClient: HttpClient
): TypedQueryJoinApplicationsSignature {
  return (query: JoinApplicationQuery) =>
    universalTypedQueryJoinApplications(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryJoinApplicationsSignature {
  /** */
  (query: JoinApplicationQuery): Promise<
    NonNullablePaths<
      QueryJoinApplicationsResponse,
      | `joinApplications`
      | `joinApplications.${number}.programId`
      | `joinApplications.${number}.status`
      | `joinApplications.${number}.paymentStatus`,
      4
    >
  >;
}

export function searchJoinApplications(
  httpClient: HttpClient
): SearchJoinApplicationsSignature {
  return (search: JoinApplicationSearch) =>
    universalSearchJoinApplications(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchJoinApplicationsSignature {
  /**
   * Searches join applications with text-based filtering across member information (email, name, nickname) and supports aggregation.
   *
   * Returns matching applications with cursor-based pagination.
   * @param - WQL expression.
   */
  (search: JoinApplicationSearch): Promise<
    NonNullablePaths<
      SearchJoinApplicationsResponse,
      | `joinApplications`
      | `joinApplications.${number}.programId`
      | `joinApplications.${number}.status`
      | `joinApplications.${number}.paymentStatus`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function bulkCreateJoinApplications(
  httpClient: HttpClient
): BulkCreateJoinApplicationsSignature {
  return (
    joinApplications: NonNullablePaths<
      JoinApplication,
      `memberId` | `programId` | `status`,
      2
    >[],
    options?: BulkCreateJoinApplicationsOptions
  ) =>
    universalBulkCreateJoinApplications(
      joinApplications,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateJoinApplicationsSignature {
  /**
   * Create multiple JoinApplications in a single request. Works synchronously.
   * @param - List of JoinApplications to be created
   */
  (
    joinApplications: NonNullablePaths<
      JoinApplication,
      `memberId` | `programId` | `status`,
      2
    >[],
    options?: BulkCreateJoinApplicationsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateJoinApplicationsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.programId`
      | `results.${number}.item.status`
      | `results.${number}.item.paymentStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkCreateJoinApplicationsApplicationErrors;
    }
  >;
}

export function bulkDeleteJoinApplications(
  httpClient: HttpClient
): BulkDeleteJoinApplicationsSignature {
  return (joinApplicationIds: string[]) =>
    universalBulkDeleteJoinApplications(
      joinApplicationIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteJoinApplicationsSignature {
  /**
   * Delete multiple JoinApplications in a single request. Works synchronously.
   * @param - JoinApplication ids to be deleted
   */
  (joinApplicationIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteJoinApplicationsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getCurrentJoinApplication(
  httpClient: HttpClient
): GetCurrentJoinApplicationSignature {
  return (programId: string) =>
    universalGetCurrentJoinApplication(
      programId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCurrentJoinApplicationSignature {
  /**
   * Returns the caller member's JoinApplication for a given program.
   *
   * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
   * @param - Program ID to look up the caller's join application for
   */
  (programId: string): Promise<
    NonNullablePaths<
      GetCurrentJoinApplicationResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: GetCurrentJoinApplicationApplicationErrors;
    }
  >;
}

export function myJoinApplication(
  httpClient: HttpClient
): MyJoinApplicationSignature {
  return (programId: string) =>
    universalMyJoinApplication(
      programId,
      // @ts-ignore
      { httpClient }
    );
}

interface MyJoinApplicationSignature {
  /**
   * Deprecated. Use [GetCurrentJoinApplication](https://dev.wix.com/docs/api-reference/business-management/online-programs/join-applications/get-current-join-application).
   *
   * Returns the caller member's JoinApplication for a given program.
   *
   * Uses implicit member identity from call scope. Returns NOT_FOUND if none exists.
   * @param - Program ID to look up the caller's join application for
   * @deprecated
   */
  (programId: string): Promise<
    NonNullablePaths<
      MyJoinApplicationResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: MyJoinApplicationApplicationErrors;
    }
  >;
}

export function approveJoinApplication(
  httpClient: HttpClient
): ApproveJoinApplicationSignature {
  return (joinApplicationId: string) =>
    universalApproveJoinApplication(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface ApproveJoinApplicationSignature {
  /**
   * Approves a JoinApplication.
   *
   * Updates the status from `APPROVAL_PENDING` to `APPROVED`.
   * Returns an error if the JoinApplication is not in `APPROVAL_PENDING` status.
   *
   * After approval, if the program requires payment, the join application's `payment_status` will be updated accordingly
   * and members will need to complete payment before becoming participants.
   * For free programs, a participant is created immediately.
   * @param - ID of the JoinApplication to approve
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      ApproveJoinApplicationResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: ApproveJoinApplicationApplicationErrors;
    }
  >;
}

export function declineJoinApplication(
  httpClient: HttpClient
): DeclineJoinApplicationSignature {
  return (joinApplicationId: string) =>
    universalDeclineJoinApplication(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeclineJoinApplicationSignature {
  /**
   * Declines a JoinApplication.
   *
   * Updates the status from APPROVAL_PENDING to DECLINED.
   * Returns an error if the JoinApplication is not in APPROVAL_PENDING status.
   * @param - ID of the JoinApplication to decline
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      DeclineJoinApplicationResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: DeclineJoinApplicationApplicationErrors;
    }
  >;
}

export function cancelJoinApplication(
  httpClient: HttpClient
): CancelJoinApplicationSignature {
  return (joinApplicationId: string) =>
    universalCancelJoinApplication(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelJoinApplicationSignature {
  /**
   * Cancels a JoinApplication.
   *
   * Updates the status from `APPROVAL_PENDING` to `CANCELLED`.
   * Can only cancel applications in `APPROVAL_PENDING` status. If payment was initiated, it will be cancelled.
   * Applications in other statuses cannot be cancelled.
   * @param - ID of the JoinApplication to cancel
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      CancelJoinApplicationResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: CancelJoinApplicationApplicationErrors;
    }
  >;
}

export function acceptInvite(httpClient: HttpClient): AcceptInviteSignature {
  return (joinApplicationId: string) =>
    universalAcceptInvite(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface AcceptInviteSignature {
  /**
   * Accepts an invitation to join a program.
   *
   * Uses implicit member identity from call scope to verify ownership of the JoinApplication,
   * or `manage_applications` permission for administrative flows, and transitions its status
   * from `INVITED` to `APPROVED`.
   *
   * After accepting the invitation, if the program requires payment, call `PreparePayment` to initiate the payment flow.
   * For free programs, a participant is created automatically and the `participant_id` will be populated.
   *
   * Returns an error if:
   * - The JoinApplication does not exist or the caller does not own it and lacks manage permission
   * - The JoinApplication is not in `INVITED` status
   * @param - ID of the JoinApplication to accept
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      AcceptInviteResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: AcceptInviteApplicationErrors;
    }
  >;
}

export function bulkUpdateJoinApplicationTags(
  httpClient: HttpClient
): BulkUpdateJoinApplicationTagsSignature {
  return (
    joinApplicationIds: string[],
    options?: BulkUpdateJoinApplicationTagsOptions
  ) =>
    universalBulkUpdateJoinApplicationTags(
      joinApplicationIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateJoinApplicationTagsSignature {
  /**
   * Updates tags for multiple JoinApplications by IDs.
   *
   * Allows assigning and/or unassigning tags in a single operation.
   * At least one of assign_tags or unassign_tags must be provided.
   * @param - JoinApplication IDs to update tags for (1-100 items)
   */
  (
    joinApplicationIds: string[],
    options?: BulkUpdateJoinApplicationTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateJoinApplicationTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateJoinApplicationTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateJoinApplicationTagsByFilter(
  httpClient: HttpClient
): BulkUpdateJoinApplicationTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateJoinApplicationTagsByFilterOptions
  ) =>
    universalBulkUpdateJoinApplicationTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateJoinApplicationTagsByFilterSignature {
  /**
   * Updates tags for JoinApplications matching a filter.
   *
   * Allows assigning and/or unassigning tags for all JoinApplications matching the filter.
   * At least one of `assign_tags` or `unassign_tags` must be provided.
   * This operation runs asynchronously.
   * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @param - WQL filter to select JoinApplications
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateJoinApplicationTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateJoinApplicationTagsByFilterResponse,
      `jobId`,
      2
    > & {
      __applicationErrorsType?: BulkUpdateJoinApplicationTagsByFilterApplicationErrors;
    }
  >;
}

export function preparePayment(
  httpClient: HttpClient
): PreparePaymentSignature {
  return (joinApplicationId: string, paymentType: PaymentTypeWithLiterals) =>
    universalPreparePayment(
      joinApplicationId,
      paymentType,
      // @ts-ignore
      { httpClient }
    );
}

interface PreparePaymentSignature {
  /**
   * Prepares payment for a JoinApplication or resolves an existing Pricing Plans entitlement.
   *
   * Before creating or refreshing checkout details, checks whether the member has
   * an eligible Pricing Plans entitlement for the program. If so, returns the
   * JoinApplication with `payment_status` set to `PAYMENT_SUCCESSFUL` and the
   * eligible plan IDs in `payment_info.paid_plan_ids`. No checkout is needed,
   * even when the application was previously in `PAYMENT_PENDING`.
   *
   * If no eligible entitlement exists, the JoinApplication must have
   * `payment_status` set to `PAYMENT_PENDING`, `PAYMENT_FAILED`, or
   * `PAYMENT_CANCELED`.
   *
   * For `SINGLE_PAYMENT`, creates a Wix Payments order and updates
   * `payment_status` to `PAYMENT_IN_PROGRESS`.
   *
   * For `PAID_PLANS`, validates that pricing plans are connected and updates
   * `payment_status` to `PAYMENT_IN_PROGRESS`.
   *
   * When called for `PAYMENT_FAILED` or `PAYMENT_CANCELED`, this retries payment
   * by replacing stale payment details with newly prepared payment details.
   *
   * Inspect the returned `payment_status` before starting checkout. Direct the
   * member to checkout only when it is `PAYMENT_IN_PROGRESS`. Use `ApplyCoupon`
   * to apply discounts before completing a Wix Payments checkout.
   * @param - ID of the JoinApplication to prepare payment for
   * @param - Type of payment to prepare
   */
  (joinApplicationId: string, paymentType: PaymentTypeWithLiterals): Promise<
    NonNullablePaths<
      PreparePaymentResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: PreparePaymentApplicationErrors;
    }
  >;
}

export function applyCoupon(httpClient: HttpClient): ApplyCouponSignature {
  return (joinApplicationId: string, couponCode: string) =>
    universalApplyCoupon(
      joinApplicationId,
      couponCode,
      // @ts-ignore
      { httpClient }
    );
}

interface ApplyCouponSignature {
  /**
   * Applies a coupon to the payment order for a JoinApplication.
   *
   * Applies a coupon code to reduce the payment amount.
   * The JoinApplication must have `payment_status` set to `PAYMENT_IN_PROGRESS`,
   * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member apply
   * or change a coupon when retrying after a failed or canceled payment.
   * Returns the updated order totals with discount applied.
   * Only one coupon can be applied per order.
   * @param - ID of the JoinApplication to apply the coupon to
   * @param - Coupon code to apply
   */
  (joinApplicationId: string, couponCode: string): Promise<
    NonNullablePaths<
      ApplyCouponResponse,
      `couponId` | `subTotal` | `discount` | `total`,
      2
    > & {
      __applicationErrorsType?: ApplyCouponApplicationErrors;
    }
  >;
}

export function removeCoupon(httpClient: HttpClient): RemoveCouponSignature {
  return (joinApplicationId: string, couponId: string) =>
    universalRemoveCoupon(
      joinApplicationId,
      couponId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveCouponSignature {
  /**
   * Removes a previously applied coupon from the payment order for a JoinApplication.
   *
   * The JoinApplication must have a prepared payment order with a coupon applied,
   * and `payment_status` set to `PAYMENT_IN_PROGRESS`, `PAYMENT_FAILED`, or
   * `PAYMENT_CANCELED`. The latter two let a member clear a coupon when retrying
   * after a failed or canceled payment.
   * Returns the updated order totals after removing the coupon.
   * @param - ID of the JoinApplication to remove the coupon from
   * @param - ID of the coupon to remove (must match the coupon applied to the order)
   */
  (joinApplicationId: string, couponId: string): Promise<
    NonNullablePaths<RemoveCouponResponse, `total`, 2> & {
      __applicationErrorsType?: RemoveCouponApplicationErrors;
    }
  >;
}

export function completeFreeCouponPayment(
  httpClient: HttpClient
): CompleteFreeCouponPaymentSignature {
  return (joinApplicationId: string) =>
    universalCompleteFreeCouponPayment(
      joinApplicationId,
      // @ts-ignore
      { httpClient }
    );
}

interface CompleteFreeCouponPaymentSignature {
  /**
   * Completes payment for a JoinApplication when a 100% discount coupon is applied.
   *
   * This endpoint verifies that a coupon has been applied and covers the full payment amount (order total is zero),
   * then marks the payment as successful and redeems the coupon.
   *
   * Prerequisites:
   * - `PreparePayment` must have been called to create a payment order
   * - `ApplyCoupon` must have been called with a coupon that covers 100% of the price
   * - The JoinApplication `payment_status` must be `PAYMENT_IN_PROGRESS`,
   * `PAYMENT_FAILED`, or `PAYMENT_CANCELED`. The latter two let a member close out
   * a free-coupon retry after a failed or canceled payment without re-running
   * `PreparePayment`.
   *
   * On success, the `payment_status` is updated to `PAYMENT_SUCCESSFUL` and a participant is created.
   * @param - ID of the JoinApplication to complete free coupon payment for
   */
  (joinApplicationId: string): Promise<
    NonNullablePaths<
      CompleteFreeCouponPaymentResponse,
      | `joinApplication.programId`
      | `joinApplication.status`
      | `joinApplication.paymentStatus`
      | `joinApplication.paymentInfo.paidPlanIds`
      | `joinApplication.tags.privateTags.tagIds`,
      5
    > & {
      __applicationErrorsType?: CompleteFreeCouponPaymentApplicationErrors;
    }
  >;
}

export function bulkInviteMembersToProgramByFilter(
  httpClient: HttpClient
): BulkInviteMembersToProgramByFilterSignature {
  return (
    programId: string,
    options?: BulkInviteMembersToProgramByFilterOptions
  ) =>
    universalBulkInviteMembersToProgramByFilter(
      programId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkInviteMembersToProgramByFilterSignature {
  /**
   * Bulk invites site members to a program with optional exclude filter.
   *
   * Creates JoinApplications in INVITED status for all site members matching the filter.
   * This operation runs asynchronously.
   * Returns a job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.
   * @param - Program to which bulk invite members.
   */
  (
    programId: string,
    options?: BulkInviteMembersToProgramByFilterOptions
  ): Promise<
    NonNullablePaths<BulkInviteMembersToProgramByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkInviteMembersToProgramByFilterApplicationErrors;
    }
  >;
}

export const onJoinApplicationCreated = /*#__PURE__*/ EventDefinition(
  'wix.online_programs.participants.v3.join_application_created',
  true,
  (event: JoinApplicationCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinApplicationCreatedEnvelope>();
export const onJoinApplicationDeleted = /*#__PURE__*/ EventDefinition(
  'wix.online_programs.participants.v3.join_application_deleted',
  true,
  (event: JoinApplicationDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinApplicationDeletedEnvelope>();
export const onJoinApplicationPaymentStatusChanged =
  /*#__PURE__*/ EventDefinition(
    'wix.online_programs.participants.v3.join_application_payment_status_changed',
    true,
    (event: JoinApplicationPaymentStatusChangedEnvelope) =>
      renameKeysFromRESTResponseToSDKResponse(
        transformPaths(event, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'data.joinApplication.createdDate' },
              { path: 'data.joinApplication.updatedDate' },
              { path: 'metadata.eventTime' },
            ],
          },
        ])
      )
  )<JoinApplicationPaymentStatusChangedEnvelope>();
export const onJoinApplicationStatusChanged = /*#__PURE__*/ EventDefinition(
  'wix.online_programs.participants.v3.join_application_status_changed',
  true,
  (event: JoinApplicationStatusChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.joinApplication.createdDate' },
            { path: 'data.joinApplication.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinApplicationStatusChangedEnvelope>();
export const onJoinApplicationTagsModified = /*#__PURE__*/ EventDefinition(
  'wix.online_programs.participants.v3.join_application_tags_modified',
  true,
  (event: JoinApplicationTagsModifiedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.joinApplication.createdDate' },
            { path: 'data.joinApplication.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<JoinApplicationTagsModifiedEnvelope>();
export const onJoinApplicationUpdated = /*#__PURE__*/ EventDefinition(
  'wix.online_programs.participants.v3.join_application_updated',
  true,
  (event: JoinApplicationUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<JoinApplicationUpdatedEnvelope>();

export {
  AcceptInviteRequest,
  AcceptInviteResponse,
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  ApplicationError,
  ApplyCouponRequest,
  ApplyCouponResponse,
  ApproveJoinApplicationRequest,
  ApproveJoinApplicationResponse,
  BaseEventMetadata,
  Behavior,
  BehaviorBehaviorOneOf,
  Benefit,
  BenefitNotification,
  BenefitType,
  BulkActionMetadata,
  BulkActionType,
  BulkCreateJoinApplicationsOptions,
  BulkCreateJoinApplicationsRequest,
  BulkCreateJoinApplicationsResponse,
  BulkDeleteJoinApplicationsRequest,
  BulkDeleteJoinApplicationsResponse,
  BulkDeleteJoinApplicationsResponseBulkJoinApplicationResult,
  BulkInviteMembersToProgramByFilterOptions,
  BulkInviteMembersToProgramByFilterRequest,
  BulkInviteMembersToProgramByFilterResponse,
  BulkJoinApplicationMigrationResult,
  BulkJoinApplicationResult,
  BulkUpdateJoinApplicationTagsByFilterOptions,
  BulkUpdateJoinApplicationTagsByFilterRequest,
  BulkUpdateJoinApplicationTagsByFilterResponse,
  BulkUpdateJoinApplicationTagsOptions,
  BulkUpdateJoinApplicationTagsRequest,
  BulkUpdateJoinApplicationTagsResponse,
  BulkUpdateJoinApplicationTagsResult,
  BulkUpsertJoinApplicationMigrationRequest,
  BulkUpsertJoinApplicationMigrationResponse,
  CancelJoinApplicationRequest,
  CancelJoinApplicationResponse,
  CompleteFreeCouponPaymentRequest,
  CompleteFreeCouponPaymentResponse,
  CreateJoinApplicationRequest,
  CreateJoinApplicationResponse,
  CreditCardPaymentMethodData,
  CurrencyAmount,
  CursorPaging,
  CursorPagingMetadata,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DeclineJoinApplicationRequest,
  DeclineJoinApplicationResponse,
  Delete,
  DeleteAllJoinApplicationsMigrationRequest,
  DeleteAllJoinApplicationsMigrationResponse,
  DeleteJoinApplicationRequest,
  DeleteJoinApplicationResponse,
  Discount,
  DiscountDiscountOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EntryPass,
  Event,
  EventMetadata,
  EventType,
  ExtendedFields,
  FullAddressContactDetails,
  GetCurrentJoinApplicationRequest,
  GetCurrentJoinApplicationResponse,
  GetJoinApplicationRequest,
  GetJoinApplicationResponse,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IncludeMissingValuesOptions,
  Initiator,
  Interval,
  ItemMetadata,
  JoinApplication,
  JoinApplicationCreatedEnvelope,
  JoinApplicationDeletedEnvelope,
  JoinApplicationMigration,
  JoinApplicationPaymentStatusChangedEnvelope,
  JoinApplicationQuerySpec,
  JoinApplicationSearchSpec,
  JoinApplicationStatusChangedEnvelope,
  JoinApplicationTagsModified,
  JoinApplicationTagsModifiedEnvelope,
  JoinApplicationUpdatedEnvelope,
  JoinApplicationsQueryBuilder,
  JoinApplicationsQueryResult,
  Member,
  MessageEnvelope,
  MissingValues,
  Mode,
  MyJoinApplicationRequest,
  MyJoinApplicationResponse,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Order,
  OrderAdditionalCharges,
  OrderItem,
  OrderItemCategory,
  Paging,
  PagingMetadataV2,
  PaymentEvent,
  PaymentEventEventOneOf,
  PaymentInfo,
  PaymentStatus,
  PaymentStatusChanged,
  PaymentType,
  PreparePaymentRequest,
  PreparePaymentResponse,
  QueryJoinApplicationsRequest,
  QueryJoinApplicationsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  RecurringPaymentCancellationDetails,
  RecurringPaymentDetails,
  RecurringPaymentStatusChangedEvent,
  RecurringPaymentStatusStatus,
  Refund,
  RefundStatus,
  RefundStatusChangedEvent,
  ReindexJoinApplicationsSearchRequest,
  ReindexJoinApplicationsSearchResponse,
  ReindexMessage,
  ReindexMessageActionOneOf,
  RemoveCouponRequest,
  RemoveCouponResponse,
  RestoreInfo,
  Results,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  ScheduledAction,
  ScheduledActionType,
  Schema,
  SearchDetails,
  SearchJoinApplicationsRequest,
  SearchJoinApplicationsResponse,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  StandardDetails,
  Status,
  StatusChanged,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  SyncPaymentExpirationTasksMigrationRequest,
  SyncPaymentExpirationTasksMigrationResponse,
  TagList,
  Tags,
  Transaction,
  TransactionCreatedEvent,
  TransactionPaymentMethodDataOneOf,
  TransactionStatus,
  TransactionStatusChangedEvent,
  TransactionUpdatedEvent,
  Upsert,
  V3AccountInfo,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  VatId,
  VatType,
  WebhookIdentityType,
  utils,
} from './online-programs-participants-v3-join-application-join-applications.universal.js';
